import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Inventory2Outlined,
  LoginRounded,
  VisibilityOffOutlined,
  VisibilityOutlined,
} from '@mui/icons-material';
import { AuthContext } from '../contexts/AuthContext';
import { useLoading } from '../contexts/LoadingContext';

export default function Login() {
  const [form, setForm] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { login } = useContext(AuthContext);
  const { start, stop } = useLoading();
  const navigate = useNavigate();

  const handleChange = (event) => {
    setForm({ ...form, [event.target.name]: event.target.value });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSubmitting(true);
    start();

    try {
      await login(form);
      navigate('/');
    } catch (err) {
      setError(err?.message || 'Error al iniciar sesión');
    } finally {
      stop();
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'grid',
        placeItems: 'center',
        px: 2,
        py: 4,
        background:
          'radial-gradient(circle at 15% 20%, rgba(37, 99, 235, 0.16), transparent 33%), radial-gradient(circle at 85% 85%, rgba(13, 148, 136, 0.12), transparent 32%)',
      }}
    >
      <Container maxWidth="xs" disableGutters>
        <Stack spacing={2.25} alignItems="center">
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box
              sx={{
                width: 48,
                height: 48,
                display: 'grid',
                placeItems: 'center',
                borderRadius: '15px',
                color: 'primary.contrastText',
                bgcolor: 'primary.main',
                boxShadow: '0 10px 24px rgba(37, 99, 235, 0.28)',
              }}
            >
              <Inventory2Outlined />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={800} lineHeight={1.1}>
                Inventario
              </Typography>
              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                CONTROL OPERATIVO
              </Typography>
            </Box>
          </Stack>

          <Card variant="outlined" sx={{ width: '100%', borderRadius: '20px' }}>
            <CardContent sx={{ p: { xs: 2.5, sm: 3.25 }, '&:last-child': { pb: { xs: 2.5, sm: 3.25 } } }}>
              <Typography variant="h5" fontWeight={800}>
                Bienvenido
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, mb: 2.5 }}>
                Ingresa tus datos para continuar con la operación.
              </Typography>

              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

              <Box component="form" onSubmit={handleSubmit} noValidate>
                <Stack spacing={1.75}>
                  <TextField
                    label="Usuario"
                    name="username"
                    value={form.username}
                    onChange={handleChange}
                    fullWidth
                    required
                    autoFocus
                    autoComplete="username"
                  />

                  <TextField
                    label="Contraseña"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={handleChange}
                    fullWidth
                    required
                    autoComplete="current-password"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword((visible) => !visible)}
                            edge="end"
                            aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                          >
                            {showPassword ? <VisibilityOffOutlined /> : <VisibilityOutlined />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    size="large"
                    sx={{ mt: 0.75 }}
                    disabled={submitting}
                    startIcon={submitting ? <CircularProgress color="inherit" size={19} /> : <LoginRounded />}
                  >
                    {submitting ? 'Ingresando...' : 'Iniciar sesión'}
                  </Button>
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Stack>
      </Container>
    </Box>
  );
}
